import { clone } from '@helpers/arrays';
import { Injectable } from '@angular/core';
import { Subscription, BehaviorSubject } from 'rxjs';
import { TableDataService } from '@services/lookup-tables/table-data.service';
import { LookupSetup } from '@models/lookup-setup';
import { LookupNode } from '@models/lookup-node';

@Injectable({ providedIn: 'root' })
export class LookupDatabase {
  subscription: Subscription = null;
  dataChange = new BehaviorSubject<LookupNode[]>([]);
  lookupTablesChange = new BehaviorSubject<LookupSetup[]>([]);
  updatedTables: any;
  treeNodes: LookupNode[];
  treeNodesRetrieved: LookupNode[];
  public categoryGroups: string[] = [];
  descriptionTable: { [key: string]: string } = {};

  get data(): LookupNode[] {
    return this.dataChange.value;
  }

  get lookupTables(): LookupSetup[] {
    return this.lookupTablesChange.value;
  }

  constructor(private tableDataService: TableDataService) {}

  public initialize(tables: LookupSetup[]) {
    const preparedData = this.preBuild(tables);
    this.treeNodes = this.buildTree(preparedData, 0);
    this.treeNodesRetrieved = this.treeNodesRetrieved || clone(this.treeNodes);
    this.dataChange.next(this.treeNodes);
    this.lookupTablesChange.next(tables);
  }

  /**
   * Builds the JSON object
   */
  private preBuild(tables: LookupSetup[]) {
    const categories = {};
    this.updatedTables = {};
    this.descriptionTable = {};
    // console.log({ tables });

    tables.forEach((table: LookupSetup) => {
      const group = table.DataTableType;
      const value = table.Mnemonic;
      const key = table.Name;

      this.updatedTables[value] = table.Update;

      if (table.Description) {
        this.descriptionTable[key] = table.Description;
      }

      if (!categories[group]) {
        categories[group] = {};
      }
      if (!this.categoryGroups.includes(group)) {
        this.categoryGroups.push(group);
      }
      categories[group][key] = value;
    });

    const newTree = { ALL: { ...categories } };
    return newTree;
  }

  /**
   * Builds the LookupNodes nested tree
   *
   * @memberof LookupDatabase
   */
  private buildTree(obj: { [key: string]: any }, level: number): LookupNode[] {
    return Object.keys(obj)
      .sort()
      .reduce<LookupNode[]>((accumulator, key) => {
        const value = obj[key];
          const node = new LookupNode();
          node.name = key;

        // Set categories flag to true for categories
        node.isCategory = this.categoryGroups.includes(node.name);

        if (value !== null) {
          if (typeof value === 'object') {
            node.children = this.buildTree(value, level + 1);
          } else {
            node.value = value;
          }
        }

        return accumulator.concat(node);
      }, []);
  }

  public getFilteredTree() {
    let treeNodes = [];
    if (this.treeNodesRetrieved) {
      const isDimensionedByScenarioTable = (node: LookupNode) =>
        this.tableDataService.dimensionedByScenarioTables.includes(node.value.toLowerCase());

      // Level 0 ("all")
      treeNodes = clone(this.treeNodesRetrieved).map(rootNode => {
        // Level 1 (children of "all", aka categories)
        rootNode.children = rootNode.children.map(categNode => {
          // Level 2 (children of categories)
          categNode.children = categNode.children.filter(child =>
            // We want to filter at this level
            this.tableDataService.scenarioSwitchState === 'scenario'
              ? isDimensionedByScenarioTable(child)
              : !isDimensionedByScenarioTable(child)
          );
          return categNode;
        });
        return rootNode;
      });
    }
    return treeNodes;
  }
}
